import type { TreeApi } from 'react-arborist';
import type { LocationTreeNode } from '../api/schedulingApis';

/** Walk the tree and return ancestor location IDs from root to parent (excluding nodeId). */
export function findAncestorIds(
  nodes: LocationTreeNode[],
  nodeId: string,
  ancestors: string[] = [],
): string[] | null {
  const targetId = String(nodeId);
  for (const node of nodes) {
    const id = node.location_id != null ? String(node.location_id) : '';
    if (id === targetId) return ancestors;
    if (node.child?.length) {
      const found = findAncestorIds(node.child, targetId, id ? [...ancestors, id] : ancestors);
      if (found) return found;
    }
  }
  return null;
}

/**
 * Expand ancestors and scroll the tree so the target node is visible.
 * Awaits arborist's virtualized list index before resolving.
 */
export async function revealLocationInTree(
  api: TreeApi<LocationTreeNode> | null | undefined,
  tree: LocationTreeNode[],
  nodeId: string,
): Promise<boolean> {
  if (!api || !nodeId) return false;
  const id = String(nodeId);
  if (findAncestorIds(tree, id) == null) return false;

  try {
    // Ensure parents are expanded so the node appears in the visible index.
    api.openParents(id);
  } catch {
    // ignore
  }

  try {
    // scrollTo already opens parents and waits until id is in idToIndex (up to ~1s).
    await Promise.resolve(api.scrollTo(id, 'center'));
    return true;
  } catch {
    return false;
  }
}
